<template>
  <v-card class="mt-4">
    <v-card-text>
      <div class="d-flex align-center mb-2">
        <p class="text-medium-emphasis text-body-2 mb-4 flex-grow-1 mb-0">
          Partagez l'accès à votre bouton avec vos proches. Les membres ajoutés pourront envoyer
          des messages audio sur ce bouton.
        </p>
        <v-btn
          v-if="isOwner"
          class="ml-2"
          color="success"
          size="small"
          @click="showAddMemberDialog = true"
        >
          <v-icon left>mdi-account-plus</v-icon>
          Ajouter un membre
        </v-btn>
      </div>

      <div v-if="loading" class="d-flex justify-center align-center" style="min-height: 100px;">
        <v-progress-circular color="primary" indeterminate />
      </div>

      <template v-else>
        <v-table class="border rounded">
          <thead>
            <tr>
              <th class="text-left">Email</th>
              <th v-if="isOwner" class="text-right" style="width: 100px;">Actions</th>
            </tr>
          </thead>

          <tbody>
            <!-- Members List -->
            <tr v-for="member in allMembers" :key="member.id">
              <td>
                {{ member.email }}
                <v-chip
                  v-if="authStore.getUserId === member.id"
                  class="ml-1"
                  color="secondary"
                  size="x-small"
                  variant="tonal"
                >
                  Vous
                </v-chip>
                <v-chip
                  v-if="device.user_id === member.id"
                  class="ml-1"
                  color="secondary"
                  size="x-small"
                  variant="tonal"
                >
                  Propriétaire
                </v-chip>
              </td>
              <td v-if="isOwner" class="text-right">
                <v-btn
                  v-if="device.user_id !== member.id"
                  color="error"
                  icon="mdi-delete-outline"
                  size="small"
                  variant="text"
                  @click="promptRemoveMember(member)"
                />
              </td>
            </tr>

            <!-- Empty State -->
            <tr v-if="allMembers.length === 0">
              <td class="text-center text-grey pa-4" :colspan="isOwner ? 2 : 1">
                Aucun membre n'a accès à ce bouton pour le moment.
              </td>
            </tr>
          </tbody>
        </v-table>
      </template>

      <!-- Dialog ajout membre -->
      <v-dialog v-model="showAddMemberDialog" max-width="400">
        <v-card>
          <v-card-title>Ajouter un membre</v-card-title>
          <v-card-text>
            <div class="text-body-2 mb-2">
              Saisissez l'adresse email de la personne à qui vous souhaitez donner accès à ce bouton.<br>
              Cette personne doit avoir préalablement créé son compte en s'inscrivant sur ce site.
            </div>
            <v-text-field
              v-model="newMemberEmail"
              autofocus
              :disabled="addingMember"
              label="Email du membre"
              :rules="[v => !!v || 'L\'email est requis']"
              type="email"
              @keyup.enter="tryAddMember"
            />
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn :disabled="addingMember" variant="text" @click="closeAddMemberDialog">Annuler</v-btn>
            <v-btn color="primary" :disabled="!newMemberEmail || addingMember" :loading="addingMember" @click="tryAddMember">Ajouter</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Dialog suppression membre -->
      <v-dialog v-model="showRemoveMemberDialog" max-width="400">
        <v-card>
          <v-card-title>Retirer le membre</v-card-title>
          <v-card-text>
            Êtes-vous sûr de vouloir retirer <strong>{{ memberToRemove?.email }}</strong> de l'accès à ce bouton ?
            Cette personne ne pourra plus y envoyer de messages audio.
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn :disabled="removingMember" variant="text" @click="closeRemoveMemberDialog">Annuler</v-btn>
            <v-btn color="error" :disabled="removingMember" :loading="removingMember" @click="confirmRemoveMember">Retirer</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

    </v-card-text>
  </v-card>
</template>

<script setup lang="ts">
  import type { Device } from '@/model/device.ts'
  import { computed, onMounted, ref } from 'vue'
  import { deviceRepository } from '@/api/repository/device.repository.ts'
  import { familyGroupRepository } from '@/api/repository/familygroup.repository.ts'
  import { type FamilyGroup, FamilyGroupMember } from '@/model/family-group.ts'
  import { useAuthStore } from '@/stores/auth.ts'
  import { ToastType, useToastStore } from '@/stores/toast.store.ts'
  import { notifyApiError } from '@/utils/error.utils.ts'

  const props = defineProps<{
    device: Device
  }>()

  const emit = defineEmits<{
    'update:device': [device: Device]
  }>()

  const authStore = useAuthStore()
  const toastStore = useToastStore()

  // State
  const loading = ref(true)
  const familyGroup = ref<FamilyGroup | null>(null)

  // State: Add Member
  const newMemberEmail = ref('')
  const addingMember = ref(false)
  const showAddMemberDialog = ref(false)

  // State: Remove Member
  const showRemoveMemberDialog = ref(false)
  const removingMember = ref(false)
  const memberToRemove = ref<FamilyGroupMember | null>(null)

  const isOwner = computed(() => {
    return props.device.user_id === authStore.getUserId
  })

  const allMembers = computed<FamilyGroupMember[]>(() => {
    if (!familyGroup.value?.members_details) {
      return [new FamilyGroupMember(authStore.getUserId, authStore.getUserEmail)]
    }
    return familyGroup.value.members_details
  })

  /**
   * Load the family group associated to this device (first one if any)
   */
  async function loadFamilyGroup () {
    loading.value = true
    try {
      const groupId = props.device.family_groups?.[0]
      familyGroup.value = groupId
        ? await familyGroupRepository.getFamilyGroup(groupId)
        : null
    } catch (error) {
      notifyApiError('Impossible de charger les membres du partage.', error)
    } finally {
      loading.value = false
    }
  }

  /**
   * Create a family group transparently (named after the device) and associate it to the device
   */
  async function ensureFamilyGroup (): Promise<FamilyGroup> {
    if (familyGroup.value) {
      return familyGroup.value
    }

    // Create group named after the device
    const created = await familyGroupRepository.createFamilyGroup({ name: props.device.name })

    // Associate group to device
    const updatedDevice = await deviceRepository.updateDevice(props.device.id, {
      name: props.device.name,
      family_groups: [created.id],
    })
    emit('update:device', updatedDevice)

    familyGroup.value = created
    return created
  }

  // --- Add Member Logic ---

  async function tryAddMember () {
    const email = newMemberEmail.value.trim()
    if (!email || !isOwner.value) {
      return
    }

    addingMember.value = true
    try {
      const group = await ensureFamilyGroup()
      await familyGroupRepository.addMember(group.id, email)

      toastStore.notify('Membre ajouté', ToastType.Success)
      newMemberEmail.value = ''
      showAddMemberDialog.value = false

      await loadFamilyGroup()
    } catch (error) {
      notifyApiError('Erreur lors de l\'ajout du membre', error)
    } finally {
      addingMember.value = false
    }
  }

  function closeAddMemberDialog () {
    showAddMemberDialog.value = false
    newMemberEmail.value = ''
  }

  // --- Remove Member Logic ---

  function promptRemoveMember (member: FamilyGroupMember) {
    memberToRemove.value = member
    showRemoveMemberDialog.value = true
  }

  function closeRemoveMemberDialog () {
    showRemoveMemberDialog.value = false
    memberToRemove.value = null
  }

  async function confirmRemoveMember () {
    if (!isOwner.value || !familyGroup.value || !memberToRemove.value) {
      return
    }

    removingMember.value = true
    try {
      await familyGroupRepository.removeMember(familyGroup.value.id, memberToRemove.value.email)
      toastStore.notify('Membre retiré', ToastType.Success)
      closeRemoveMemberDialog()
      await loadFamilyGroup()
    } catch (error) {
      notifyApiError('Erreur lors de la suppression du membre', error)
    } finally {
      removingMember.value = false
    }
  }

  onMounted(() => {
    loadFamilyGroup()
  })
</script>

<style scoped>
.v-table :deep(thead) {
  background-color: rgba(var(--v-theme-on-surface), 0.02);
}

:deep(.v-card-title) {
  white-space: normal !important;
  overflow: visible !important;
  text-overflow: unset !important;
}
</style>
